import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n-bridge';
import type {
    ProofreadingIssue,
    ProofreadingIssueLocation,
    ProofreadingIssueStatus,
} from '@/types/proofreading';
import {
    scrollToElementTop,
    SCROLL_INTO_VIEW_DURATION_MS,
} from '@/lib/utils/proofreadingEditorUtil';
import IconChevronDown from '~icons/figma/ChevronDown';


export default /*#__PURE__*/_defineComponent({
  __name: 'IssueItem',
  props: {
    issue: { type: null, required: true },
    isActive: { type: Boolean, required: false },
    activeLocationIds: { type: Array, required: false },
    categoryLabel: { type: String, required: false },
    defaultOpen: { type: Boolean, required: false }
  },
  emits: ["click", "apply", "revert", "reject-toggle", "hover"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'click', payload: { issueId: string; episodeKey: string }): void;
    (event: 'apply', issue: ProofreadingIssue): void;
    (event: 'revert', issue: ProofreadingIssue): void;
    (event: 'reject-toggle', issue: ProofreadingIssue): void;
    (event: 'hover', issue: ProofreadingIssue, isHovered: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    issue: ProofreadingIssue;
    isActive?: boolean;
    activeLocationIds?: string[];
    categoryLabel?: string;
    defaultOpen?: boolean;
};

const { t } = useI18n();





const applyLabel = computed(() => t('proofreadingReviseManuscriptPage.apply'));
const markDoneLabel = computed(() => t('proofreadingReviseManuscriptPage.markDone'));
const revertLabel = computed(() => t('proofreadingReviseManuscriptPage.revert'));
const isRejected = computed(() => props.issue.status === 'rejected');
const rejectLabel = computed(() =>
    t(
        isRejected.value
            ? 'proofreadingReviseManuscriptPage.rejected'
            : 'proofreadingReviseManuscriptPage.reject',
    ),
);

const hasLocations = computed(
    () => (props.issue.locations?.length ?? 0) > 0,
);

const rootRef = ref<HTMLElement | null>(null);
const isExpanded = ref(props.defaultOpen ?? false);

function scrollToAccordionTop() {
    nextTick(() => {
        if (!rootRef.value) return;
        scrollToElementTop(rootRef.value, SCROLL_INTO_VIEW_DURATION_MS);
    });
}

function expandIfActiveLocationSelected() {
    if (!hasLocations.value) return;
    const locationIds = props.activeLocationIds;
    if (locationIds == null || locationIds.length === 0) return;

    const hasActiveLocation = props.issue.locations?.some(
        (loc) => locationIds.includes(loc.id),
    );
    if (hasActiveLocation) {
        isExpanded.value = true;
    }
}

watch(
    () => props.activeLocationIds,
    () => {
        expandIfActiveLocationSelected();
    },
    { deep: true },
);

watch(
    () => props.isActive,
    (active, wasActive) => {
        if (active && wasActive === false && hasLocations.value) {
            isExpanded.value = true;
        }
    },
);

const displayIssueTypeLabel = computed(
    () => props.issue.issueTypeLabel ?? props.categoryLabel ?? '',
);

const headlineText = computed(() => {
    const typeLabel = displayIssueTypeLabel.value;
    if (props.issue.manuscriptLevel) {
        return `${typeLabel}${t('proofreadingReviseManuscriptPage.manuscriptLevelPositionParen')}`;
    }
    const location =
        props.issue.line > 0
            ? t('proofreadingReviseManuscriptPage.locationPositionParen', {
                line: props.issue.line,
                column: props.issue.column,
            })
            : '';
    return `${typeLabel}${location}`;
});

const displaySummary = computed(() => {
    const variants = props.issue.variationVariants;
    if (!variants?.length) return props.issue.summary;

    return variants
        .map((variant) =>
            t('proofreadingReviseManuscriptPage.variationVariantPart', {
                text: variant.text,
                count: variant.count,
            }),
        )
        .join(' / ');
});

const displaySuggestionText = computed(() =>
    props.issue.suggestionSegments.map((seg) => seg.text).join(''),
);

function formatLocationRowPosition(location: ProofreadingIssueLocation) {
    if (location.episodeTitle) {
        return t('proofreadingReviseManuscriptPage.location', {
            episode: location.episodeTitle,
            line: location.line,
            column: location.column,
        });
    }
    return t('proofreadingReviseManuscriptPage.locationPosition', {
        line: location.line,
        column: location.column,
    });
}

function getLocationStatus(location: ProofreadingIssueLocation): ProofreadingIssueStatus {
    return location.status ?? 'pending';
}

function getLocationCandidates(): string[] {
    return (props.issue.variationVariants ?? []).map((variant) => variant.text);
}

function getLocationRejectLabel(location: ProofreadingIssueLocation): string {
    return t(
        getLocationStatus(location) === 'rejected'
            ? 'proofreadingReviseManuscriptPage.rejected'
            : 'proofreadingReviseManuscriptPage.reject',
    );
}

function buildLocationIssuePayload(
    location: ProofreadingIssueLocation,
    replacementText?: string,
): ProofreadingIssue {
    return {
        ...props.issue,
        id: location.id,
        episodeKey: location.episodeKey,
        line: location.line,
        column: location.column,
        text: location.text,
        ...(replacementText != null ? { replacementText } : {}),
    };
}

function onClickCard() {
    if (hasLocations.value) {
        isExpanded.value = !isExpanded.value;
        if (isExpanded.value) {
            scrollToAccordionTop();
        }
    }
    emit('click', { issueId: props.issue.id, episodeKey: props.issue.episodeKey });
}

function onClickLocation(location: ProofreadingIssueLocation) {
    emit('click', { issueId: location.id, episodeKey: location.episodeKey });
}

function onApply() {
    emit('apply', props.issue);
}

function onRevert() {
    emit('revert', props.issue);
}

function onRejectToggle() {
    emit('reject-toggle', props.issue);
}

function onApplyLocation(location: ProofreadingIssueLocation, candidate: string) {
    emit('apply', buildLocationIssuePayload(location, candidate));
}

function onRevertLocation(location: ProofreadingIssueLocation) {
    emit('revert', buildLocationIssuePayload(location));
}

function onRejectToggleLocation(location: ProofreadingIssueLocation) {
    emit('reject-toggle', buildLocationIssuePayload(location));
}

function onMouseEnterCard() {
    if (hasLocations.value) return;
    emit('hover', props.issue, true);
}

function onMouseLeaveCard() {
    if (hasLocations.value) return;
    emit('hover', props.issue, false);
}

function onMouseEnterLocation(location: ProofreadingIssueLocation) {
    emit('hover', buildLocationIssuePayload(location), true);
}

function onMouseLeaveLocation(location: ProofreadingIssueLocation) {
    emit('hover', buildLocationIssuePayload(location), false);
}

return { __sfc: true,t, props, emit, applyLabel, markDoneLabel, revertLabel, isRejected, rejectLabel, hasLocations, rootRef, isExpanded, scrollToAccordionTop, expandIfActiveLocationSelected, displayIssueTypeLabel, headlineText, displaySummary, displaySuggestionText, formatLocationRowPosition, getLocationStatus, getLocationCandidates, getLocationRejectLabel, buildLocationIssuePayload, onClickCard, onClickLocation, onApply, onRevert, onRejectToggle, onApplyLocation, onRevertLocation, onRejectToggleLocation, onMouseEnterCard, onMouseLeaveCard, onMouseEnterLocation, onMouseLeaveLocation, IconChevronDown }
}

})